import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  AlertController,
  ModalController,
  PopoverController,
} from '@ionic/angular';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { TeamService } from '../../_core/services/team.service';
import { ToastService } from '../../_core/services/toast.service';
import { Team } from '../../_shared/models/team.model';
import { usaStates } from 'typed-usa-states/dist/states';
import { ColorPickerComponent } from './color-picker/color-picker.component';
import { ItaTeamGetterComponent } from './ita-team-getter/ita-team-getter.component';
import { SearchTeamListComponent } from '../../_shared/components/search-team-list/search-team-list.component';
import { TranslateService } from '@ngx-translate/core';
interface ImageUploadedData {
  image: any;
  imageChanged: boolean;
}

@Component({
  standalone: false,
  selector: 'app-create-team',
  templateUrl: './create-team.component.html',
  styleUrls: ['./create-team.component.scss'],
})
export class CreateTeamComponent implements OnInit, OnDestroy {
  @Input() team: Team;
  @Input() isEdit: boolean;
  @Input() leagueId: string;
  @Input() partner: string | undefined;
  @Input({ required: false }) isTypeDisabled: boolean = false;
  @Input({ required: false }) isHighSchool: boolean = false;
  @Input() closeAllModalsOnSuccess = true;
  @Input() ITAlogo: string | undefined;
  private readonly translateService = inject(TranslateService);


  form: UntypedFormGroup;
  states = usaStates;

  private readonly destroy$: Subject<void> = new Subject();
  isSubmitting: boolean;
  credentials: {
    username: string;
    password: string;
    partner: string;
  };
  isItaLoginPresented: boolean;
  private _userImage: ImageUploadedData;

  constructor(
    private fb: UntypedFormBuilder,
    private toast: ToastService,
    private modalController: ModalController,
    private teamService: TeamService,
    private alertController: AlertController
  ) {}

  ngOnInit() {
    if (this.team?.organisations?.length) {
      this.partner = this.team?.organisations[0]?.organisationAbbreviation;
    }
    this.buildForm();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  buildForm() {
    this.form = this.fb.group({
      name: [this.team?.name || '', Validators.required],
      abbreviation: [
        this.team?.abbreviation || '',
        [Validators.required, Validators.maxLength(8)],
      ],
      colors: [this.team?.colors[0] || '', Validators.required],
      logo: [this.team?.logo || ''],
      type: [this.isHighSchool ? 'high-school' : this.team?.type?.value || 'other', Validators.required],
      subtype: [this.team?.subtype?.value || 'other', Validators.required],
      teamId: [this.team?.teamId || ''],
      description: [this.team?.description || ''],
    });

    this.form.get('name')?.valueChanges.subscribe((value) => {
      if (this.form.get('abbreviation')?.pristine) {
        this.form.get('abbreviation')?.setValue(this.getInitials(value));
      }
    });

    this.form.get('type')?.valueChanges.subscribe(async (value) => {
      if (value === 'college') {
        await this.openSearchModal();
        this.form.get('subtype')?.setValue('');
      } else {
        this.form.get('subtype')?.setValue('other');
      }
    });
  }

  async getTeamColor() {
    const modal = await this.modalController.create({
      component: ColorPickerComponent,
      componentProps: {
        color: this.form.get('colors')?.value,
      },
      presentingElement: await this.modalController.getTop(),
    });

    await modal.present();

    const { data } = await modal.onDidDismiss();

    if (data) {
      this.form.get('colors')?.setValue(data);
    }
  }

  /* onSubmit the form add and update team. */
  async onSubmit() {
    if (this.form.invalid) {
      if (this.form.get('name')?.invalid)
        this.toast.presentToast('Team name is required');
      else if (this.form.get('abbreviation')?.invalid)
        this.toast.presentToast('Abbreviation is required');
      else if (this.form.get('colors')?.invalid)
        this.toast.presentToast('Color is required');
      else if (this.form.get('subtype')?.invalid)
        this.toast.presentToast('Division is required');
      return;
    }
    this.isSubmitting = true;

    const createTeamData = this.form.value;
    createTeamData.type = this.typeParser(createTeamData.type);
    createTeamData.subtype = this.subtypeParser(
      createTeamData.type.value,
      createTeamData.subtype
    );

    // Create a FormData object
    const formData = new FormData();
    formData.append('teamData', JSON.stringify(createTeamData));

    // Handle profile_image if it's available
    if (this._userImage?.image) {
      const blob = await fetch(this._userImage?.image).then((res) =>
        res.blob()
      );
      formData.append('profile_image', blob);
    } else {
      formData.append('profile_image', ''); // Add empty string if no image
    }

    // Append isImageChange
    formData.append(
      'isImageChange',
      this._userImage?.imageChanged ? 'true' : 'false'
    );
    formData.append('partner', this.partner ? this.partner : "undefined");
    formData.append('leagueId', this.leagueId ? this.leagueId : "undefined");

    const request$ = this.isEdit
      ? this.teamService.update(this.team._id, formData)
      : this.teamService.create(formData);

    request$
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isSubmitting = false))
      )
      .subscribe({
        next: async(res) => {
          const team = res.data;
          if (this.closeAllModalsOnSuccess) {
            let topModal = await this.modalController.getTop();
            let isFirstDismissal = true;
            while (topModal) {
              await this.modalController.dismiss(isFirstDismissal ? team : undefined);
              isFirstDismissal = false;
              topModal = await this.modalController.getTop();
            }
          } else {
            await this.modalController.dismiss(team);
          }
        },
        error: (err) => {
            //console.log(err)
        }
      });
  }

  private typeParser(typeValue) {
    if (typeValue === 'college') {
      return {
        value: typeValue,
        name: 'College',
      };
    } else if (typeValue === 'high-school') {
      return {
        value: typeValue,
        name: 'High School',
      };
    } else {
      return {
        value: typeValue,
        name: 'Other',
      };
    }
  }

  private subtypeParser(typeValue, subTypeValue: string) {
    if (typeValue === 'high-school') {
      return {
        value: subTypeValue,
        name: this.states.find((state) => state.abbreviation === subTypeValue)
          ?.name,
      };
    } else if (typeValue === 'college') {
      let name = '';
      if (subTypeValue === 'd1') name = 'Division 1';
      else if (subTypeValue === 'd1') name = 'Division 1';
      else if (subTypeValue === 'd2') name = 'Division 2';
      else if (subTypeValue === 'd3') name = 'Division 3';
      else if (subTypeValue === 'juco') name = 'JUCO';
      else if (subTypeValue === 'naia') name = 'NAIA';

      return {
        value: subTypeValue,
        name: name,
      };
    }

    return 'None';
  }

  dismiss() {
    this.modalController.dismiss();
  }

  public getInitials(teamName: string) {
    if (teamName) {
      let abbreviation = '';
      const array = teamName.split(' ');
      array.forEach((element) => {
        if (element.length > 2 && abbreviation.length < 3) {
          abbreviation += element.charAt(0).toUpperCase();
        }
      });
      return abbreviation;
    }
  }

  async presentItaCoachAlert() {
    const alert = await this.alertController.create({
      header: 'Are you ITA coach?',
      message:
        'If you are ITA coach you can log in with your credentials to pull your schedule and roster. Do you want to continue?',
      buttons: [
        {
          text: 'Yes',
          handler: async () => {
            await this.showItaLoginPopover();
          },
        },
        {
          text: 'No',
        },
      ],
    });

    await alert.present();
  }

  async showItaLoginPopover() {
    this.isItaLoginPresented = true;

    const modal = await this.modalController.create({
      component: ItaTeamGetterComponent,
      presentingElement: await this.modalController.getTop(),
    });

    await modal.present();

    const { data } = await modal.onDidDismiss();

    if (data) {
      this.form.get('name')?.setValue(data.team.groupName);
      this.form.get('teamId')?.setValue(data.team.groupId);
      this.partner = data.partner;
      this.credentials = data.credentials;
    }
  }

  /* Selected image data in medium, original, thumbnail urls in profile_image{} object.  */
  imageSelected(data: ImageUploadedData) {
    this._userImage = data;
  }

    async openSearchModal(){
    const modal = await this.modalController.create({
      component: SearchTeamListComponent,
      componentProps: {
        iaItaResultsPage: true,
        emptyPagePlaceholder: this.translateService.instant('Search teams to show results. Once you find your team go to staff page to claim team.'),
      },
    });
    await modal.present();
  }
}
